import { ApiError, AuthenticationError, AuthenticationRequiredError } from "./errors";

/** `timeout` replaces the default request deadline, in milliseconds. */
export interface ApiRequestInit extends RequestInit {
  readonly timeout?: number;
}

export interface AuthenticatedTransport {
  authenticatedFetch(path: string, init?: ApiRequestInit): Promise<Response>;
}

// Retain the request signal through body consumption, including custom abort reasons.
const responseSignals = new WeakMap<Response, AbortSignal>();

export async function fetchResponse(url: string, init: ApiRequestInit = {}): Promise<Response> {
  const { timeout = 20_000, ...request } = init;
  const deadline = AbortSignal.timeout(timeout);
  const signal = request.signal ? AbortSignal.any([request.signal, deadline]) : deadline;
  try {
    signal.throwIfAborted();
    const response = await fetch(url, { ...request, cache: "no-store", signal });
    signal.throwIfAborted();
    responseSignals.set(response, signal);
    return response;
  } catch (error) {
    throwRequestError(error, signal);
  }
}

export async function requestJson<T>(
  transport: AuthenticatedTransport,
  path: string,
  parser: (value: unknown) => T,
  kind: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body !== undefined) headers.set("Content-Type", "application/json");
  let response: Response;
  try {
    init.signal?.throwIfAborted();
    response = await transport.authenticatedFetch(path, { ...init, headers });
    init.signal?.throwIfAborted();
  } catch (error) {
    throwRequestError(error, init.signal);
  }
  return parseJsonResponse(response, parser, kind, init.signal);
}

export async function parseJsonResponse<T>(
  response: Response,
  parser: (value: unknown) => T,
  kind: string,
  signal?: AbortSignal | null,
): Promise<T> {
  await requireStatus(response, undefined, signal);
  const value = await readJson(response, signal);
  (responseSignals.get(response) ?? signal)?.throwIfAborted();
  try {
    return parser(value);
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new ApiError(`The server returned invalid ${kind} data: ${detail}`, response.status, {
      cause: error,
    });
  }
}

export async function requireStatus(
  response: Response,
  expectedStatus?: number,
  signal?: AbortSignal | null,
): Promise<void> {
  (responseSignals.get(response) ?? signal)?.throwIfAborted();
  if (expectedStatus === undefined ? response.ok : response.status === expectedStatus) return;
  throw new ApiError(await responseError(response, signal), response.status);
}

export async function responseError(
  response: Response,
  signal?: AbortSignal | null,
): Promise<string> {
  try {
    const value = await readJson(response, signal);
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const error = (value as Record<string, unknown>).error;
      if (typeof error === "string" && error.length > 0 && error.length <= 500) return error;
    }
  } catch (error) {
    // A malformed error body still has an actionable HTTP status.
    if (!(error instanceof ApiError && error.cause instanceof SyntaxError)) throw error;
  }
  return "Request failed.";
}

async function readJson(
  response: Response,
  callerSignal?: AbortSignal | null,
): Promise<unknown> {
  const signal = responseSignals.get(response) ?? callerSignal;
  try {
    signal?.throwIfAborted();
    const value: unknown = await response.json();
    signal?.throwIfAborted();
    return value;
  } catch (error) {
    signal?.throwIfAborted();
    if (error instanceof SyntaxError) {
      throw new ApiError("The server returned invalid JSON.", response.status, {
        cause: error,
      });
    }
    throwRequestError(error, signal, response.status);
  }
}

function throwRequestError(
  error: unknown,
  signal?: AbortSignal | null,
  status: number | null = null,
): never {
  signal?.throwIfAborted();
  if (
    error instanceof ApiError ||
    error instanceof AuthenticationRequiredError ||
    error instanceof AuthenticationError ||
    (error instanceof Error && (error.name === "AbortError" || error.name === "TimeoutError"))
  ) {
    throw error;
  }
  const detail = error instanceof Error ? error.message : String(error);
  throw new ApiError(
    status === null
      ? `Network request failed (no HTTP response): ${detail}`
      : `Response body read failed: ${detail}`,
    status,
    { cause: error },
  );
}
